// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (c) 2026 Password Manager contributors
/**
 * Application Configuration
 *
 * Centralized configuration using environment variables.
 * Provides type-safe access to environment-specific settings.
 *
 * @module lib/config
 */

import { apiConfig } from "./api/config";

/**
 * Application configuration object
 * All values are loaded from environment variables with safe defaults
 */
export const config = {
  /**
   * Base site URL (from VITE_SITE_URL environment variable)
   * Used for absolute URLs in SEO meta tags, sitemaps, and structured data
   *
   * @example "https://pssmngr.com"
   * @default "https://pssmngr.com"
   */
  siteUrl:
    import.meta.env.VITE_SITE_URL ||
    (import.meta.env.DEV
      ? "http://localhost:41789"
      : "https://pssmngr.com"),

  /**
   * API configuration (Password Manager Dashboard)
   * Used for vault management and authentication
   */
  api: {
    /** API base URL — resolved dynamically via apiConfig */
    apiUrl: apiConfig.baseUrl,
    /** API request timeout in ms */
    timeout: 30000,
    /** Whether API integration is configured */
    isConfigured: Boolean(import.meta.env.VITE_API_URL),
  },

  /**
   * Browser-extension integration.
   *
   * `id` is the published extension ID (from VITE_EXTENSION_ID). The dashboard
   * uses it to message the installed extension (`chrome.runtime.sendMessage`)
   * to verify a deep-link pairing request before auto-granting vault access.
   * When unset (ID not yet published, or on browsers without the messaging
   * API) the pairing page safely falls back to an explicit confirm step.
   */
  extension: {
    id: import.meta.env.VITE_EXTENSION_ID || "",
  },

  /**
   * Environment mode
   * Automatically detected from Vite
   */
  isDev: import.meta.env.DEV,
  isProd: import.meta.env.PROD,
  mode: import.meta.env.MODE,
} as const;

/**
 * Validate required environment variables on module load
 * Logs warnings in development to catch configuration issues early
 */
if (import.meta.env.DEV) {
  if (!config.api.isConfigured) {
    console.warn(
      "[Config] VITE_API_URL is not set. Using default: http://localhost:38291"
    );
  }
}

/**
 * Type for config object (for external imports)
 */
export type Config = typeof config;

/**
 * Feature flags
 * Controls platform-wide behavior based on environment variables
 */
export const features = {
  /**
   * Public transcripts mode
   * When true: Users see ALL transcripts from ALL users (community mode)
   * When false: Users see ONLY their own transcripts (private mode - default)
   */
  publicTranscripts: import.meta.env.VITE_PUBLIC_TRANSCRIPTS?.trim() === "true",

  /**
   * Payments master switch (VITE_FEATURE_PAYMENTS environment variable)
   *
   * Controls the ENTIRE payments/billing/pricing/upgrade surface across the app
   * (landing page, marketing/comparison pages, and the authenticated dashboard).
   *
   * When false (the default): no pricing section, no upgrade buttons, no
   * subscription/billing pages, and no trial/past-due banners — the app appears
   * fully free. The backend independently enforces this via FEATURE_FLAG_PAYMENTS.
   *
   * @default false
   */
  payments: import.meta.env.VITE_FEATURE_PAYMENTS?.trim() === "true",

  /**
   * Open-source master switch (VITE_FEATURE_OPEN_SOURCE environment variable)
   *
   * Controls every VISIBLE open-source surface on the site: the open-source
   * landing section, the "Source code"/GitHub footer links, the open-source
   * proof stat, the open-source FAQ item, the comparison "open source" row, and
   * the open-source wording baked into meta/hero/CTA copy (landing + vs-* pages).
   *
   * Defaults ON (opt-out) so existing deploys are unchanged unless explicitly
   * disabled. Set VITE_FEATURE_OPEN_SOURCE=false to hide all of it — e.g. when
   * the source repository is no longer public.
   *
   * @default true
   */
  openSource: import.meta.env.VITE_FEATURE_OPEN_SOURCE?.trim() !== "false",
} as const;

/**
 * Type for features object
 */
export type Features = typeof features;

/**
 * Resolve an i18n key while honoring the open-source switch.
 *
 * When open-source is enabled, returns the key unchanged. When disabled,
 * returns `["<key>_noOss", "<key>"]` so i18next prefers a `_noOss` variant if
 * one exists and otherwise falls back to the original — letting copy that
 * mentions open source be rewritten without one variant per string everywhere.
 *
 * Usage: `t(ossKey("hero.badge"))`
 */
export function ossKey(key: string): string | [string, string] {
  return features.openSource ? key : [`${key}_noOss`, key];
}
